import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BatchLead, BatchLeadsFetcher } from './types';
import type { LeadRowSyncStore } from './useLeadRowSync';
import type { LeadNoteSyncStore } from './useLeadNoteSync';
import { overlayNoteSaves } from './leadNotes';
import { loadAllActiveLeads } from './loadAllLeads';

/**
 * The unified CRM grid's lead rows (port plan step 6, fix F-F): ONE load of
 * every active lead at the event, shared by the tab numbers (D9: Clean's
 * rule-held term reads the rows) and by the Clean / Match / Send lenses, which
 * render these rows instead of paging through the event themselves.
 *
 * Hosted by `BatchScreen`, keyed by that screen and the conference, read once and kept
 * (`staleTime: Infinity`). It is re-read only on `reload`: Refresh, after a
 * send, after the Details drawer's edit, when the lead drawer closes, and
 * after a move, a bulk run or a restore. Never for an edit: every change the
 * grid makes is written through into these rows instead — confirmed field
 * saves and note saves (the screen's stores' `onConfirmed`), tags and
 * assignment (`patchRow`) — so a lens never shows a row older than an edit
 * the operator saw land, whatever the Leads tab's own loads have since let
 * the stores forget. A change written while a load is out is laid back over
 * what that load returns, in case its read predates it.
 */

/**
 * The query key: one per screen instance and conference. The instance part
 * matters: the rows carry the epochs of the stores the screen that loaded
 * them hosts, so another BatchScreen (a later mount, with stores of its own)
 * must never be handed them from the cache — it would prune its own stores
 * with them. The rows are dropped from the cache when their screen goes.
 */
export function gridLeadsKey(instance: string, conferenceId: string) {
  return ['crmGridLeads', instance, conferenceId] as const;
}

let nextInstance = 0;

/** What the shared load holds. */
export interface GridLeadRowsData {
  leads: BatchLead[];
  /**
   * The stores' epochs when this load started: a lens adopting these rows
   * may let the stores forget saves confirmed up to here, which the rows
   * carry (read, or written through).
   */
  fieldEpoch: number;
  noteEpoch: number;
}

export interface GridLeadRows {
  /** Every active lead at the event; undefined until the first load lands. */
  data: GridLeadRowsData | undefined;
  /** No rows yet, and none failed: a load is out (or about to be). */
  loading: boolean;
  /** The last load failed (any rows shown are from before it). */
  failed: boolean;
  /** Re-read every page. */
  reload: () => void;
  /** Write a change to one lead's row into the shared rows (every cached event). */
  patchRow: (leadId: string, patch: (row: BatchLead) => BatchLead) => void;
}

interface PatchLogEntry {
  seq: number;
  leadId: string;
  patch: (row: BatchLead) => BatchLead;
}

export function useGridLeadRows({
  enabled,
  conferenceId,
  fetchLeads,
  leadSync,
  noteSync,
}: {
  enabled: boolean;
  conferenceId: string | null;
  fetchLeads: BatchLeadsFetcher;
  leadSync: LeadRowSyncStore;
  noteSync: LeadNoteSyncStore;
}): GridLeadRows {
  const queryClient = useQueryClient();
  const [instance] = useState(() => `screen-${++nextInstance}`);
  const fetchRef = useRef(fetchLeads);
  fetchRef.current = fetchLeads;
  // Changes written through while a load may be out, in order, and where
  // each load still out started in that order.
  const logRef = useRef<{ seq: number; entries: PatchLogEntry[]; outSince: number[] }>({
    seq: 0,
    entries: [],
    outSince: [],
  });

  const query = useQuery({
    queryKey: gridLeadsKey(instance, conferenceId ?? ''),
    // `signal`: react-query calls a load off when a newer re-read supersedes
    // it (an invalidate while it is out); its remaining pages are never asked for.
    queryFn: async ({ signal }): Promise<GridLeadRowsData> => {
      const log = logRef.current;
      const since = log.seq;
      const fieldEpoch = leadSync.epoch();
      const noteEpoch = noteSync.epoch();
      log.outSince.push(since);
      let leads: BatchLead[];
      try {
        leads = await loadAllActiveLeads(fetchRef.current, conferenceId as string, signal);
      } finally {
        log.outSince.splice(log.outSince.indexOf(since), 1);
        // A change no load still out could have missed is in every later read.
        const oldestOut = Math.min(since, ...log.outSince);
        log.entries = log.entries.filter((e) => e.seq > oldestOut);
      }
      const late = log.entries.filter((e) => e.seq > since);
      if (late.length > 0) {
        const byId = new Map<string, PatchLogEntry[]>();
        for (const e of late) byId.set(e.leadId, [...(byId.get(e.leadId) ?? []), e]);
        leads = leads.map((row) => (byId.get(row.id) ?? []).reduce((r, e) => e.patch(r), row));
      }
      return { leads, fieldEpoch, noteEpoch };
    },
    enabled: enabled && conferenceId !== null,
    staleTime: Infinity,
  });

  const patchRow = useCallback(
    (leadId: string, patch: (row: BatchLead) => BatchLead) => {
      const log = logRef.current;
      log.seq += 1;
      log.entries.push({ seq: log.seq, leadId, patch });
      queryClient.setQueriesData<GridLeadRowsData>({ queryKey: ['crmGridLeads', instance] }, (prev) => {
        if (!prev || !prev.leads.some((l) => l.id === leadId)) return prev;
        return { ...prev, leads: prev.leads.map((l) => (l.id === leadId ? patch(l) : l)) };
      });
    },
    [queryClient, instance],
  );

  // This screen's rows go with it (see `gridLeadsKey`).
  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: ['crmGridLeads', instance] });
    },
    [queryClient, instance],
  );

  // Write every confirmed save through, for as long as the grid is on.
  useEffect(() => {
    if (!enabled) return;
    const offFields = leadSync.onConfirmed((leadId, values) => patchRow(leadId, (row) => ({ ...row, ...values })));
    const offNotes = noteSync.onConfirmed((leadId, save) =>
      patchRow(leadId, (row) => overlayNoteSaves([row], new Map([[leadId, save]]))[0]),
    );
    return () => {
      offFields();
      offNotes();
    };
  }, [enabled, leadSync, noteSync, patchRow]);

  const reload = useCallback(() => {
    if (!enabled || conferenceId === null) return;
    void queryClient.invalidateQueries({ queryKey: gridLeadsKey(instance, conferenceId) });
  }, [enabled, conferenceId, queryClient, instance]);

  const failed = query.isError && !query.isFetching;
  return {
    data: query.data,
    loading: query.data === undefined && !failed,
    failed,
    reload,
    patchRow,
  };
}
